import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Users, Eye, UserPlus, Percent, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate } from "@/lib/api";
import { StatCard, Spinner } from "@/components/shared";
import { formatApiErrorDetail, useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

function Table({ title, rows, cols, testid }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testid}>
      <h2 className="font-display font-bold text-slate-900 mb-3">{title}</h2>
      {!rows?.length ? <p className="text-sm text-slate-400">No data yet.</p> : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-400">
            {cols.map((c) => <th key={c.key} className={`pb-2 font-semibold ${c.num ? "text-right" : ""}`}>{c.label}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                {cols.map((c) => <td key={c.key} className={`py-2 ${c.num ? "text-right tabular-nums" : "text-slate-700 truncate max-w-[220px]"}`}>{c.fmt ? c.fmt(r[c.key]) : r[c.key] ?? "Unknown"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

const VIS = [{ key: "name", label: "Name" }, { key: "visitors", label: "Visitors", num: true }, { key: "views", label: "Views", num: true }];

export default function Traffic() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(false);
  const sendNow = async () => {
    setSending(true);
    try { const { data } = await api.post("/analytics/weekly-email/send"); toast.success(`Weekly report sent to ${data.to}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSending(false);
  };
  useEffect(() => {
    setD(null);
    api.get(`/analytics/summary?days=${days}`).then((r) => setD(r.data)).catch((e) => setD({ error: formatApiErrorDetail(e.response?.data?.detail) }));
  }, [days]);

  const t = d?.totals;
  if (user && !user.is_platform_admin) return <Navigate to="/dashboard" replace />;
  return (
    <div className="space-y-6 min-w-0" data-testid="traffic-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Traffic</h1>
          <p className="text-slate-500 mt-1 text-sm" data-testid="traffic-since">Where ConsignOut visitors come from. {d?.tracking_since ? `Tracking since ${fmtDate(d.tracking_since)}.` : "Tracking just started. Visits before today weren't recorded."} Your own visits and bots aren't counted. A summary is emailed to you every Monday at 6am Mountain time.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <button data-testid="traffic-send-email-btn" onClick={sendNow} disabled={sending}
          className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 bg-white hover:bg-slate-50 px-3 py-2 rounded-lg disabled:opacity-50 transition-colors">
          <Mail className="w-4 h-4" /> {sending ? "Sending…" : "Email me this week's report"}
        </button>
        <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-white" data-testid="traffic-range">
          {[7, 30, 90].map((n) => (
            <button key={n} data-testid={`traffic-range-${n}`} onClick={() => setDays(n)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${days === n ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>{n} days</button>
          ))}
        </div>
        </div>
      </div>
      {!d ? <Spinner /> : d.error ? <p className="text-sm text-rose-700" data-testid="traffic-error">{d.error}</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testid="traffic-visitors" label="Visitors" value={t.visitors} icon={Users} />
            <StatCard testid="traffic-views" label="Page views" value={t.views} icon={Eye} />
            <StatCard testid="traffic-signups" label="Sign-ups" value={t.signups} icon={UserPlus} />
            <StatCard testid="traffic-conversion" label="Visitor → sign-up" value={t.visitors ? `${((t.signups / t.visitors) * 100).toFixed(1)}%` : "0%"} icon={Percent} />
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="traffic-daily">
            <h2 className="font-display font-bold text-slate-900 mb-3">Visitors per day</h2>
            <div style={{ width: "100%", height: 220, minHeight: 220 }}>
              <ResponsiveContainer>
                <BarChart data={d.daily}><XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} /><Tooltip /><Bar dataKey="visitors" fill="#059669" radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-4">
            <Table testid="traffic-sources" title="Where visitors come from" rows={d.sources} cols={[{ key: "name", label: "Source" }, { key: "visitors", label: "Visitors", num: true }, { key: "signups", label: "Sign-ups", num: true }]} />
            <Table testid="traffic-pages" title="Top pages" rows={d.pages} cols={VIS} />
            <Table testid="traffic-countries" title="Countries" rows={d.countries} cols={VIS} />
            <Table testid="traffic-sites" title="Site address" rows={d.sites} cols={VIS} />
            <Table testid="traffic-devices" title="Devices" rows={d.devices} cols={VIS} />
            <Table testid="traffic-campaigns" title="Campaigns (utm_campaign)" rows={d.campaigns} cols={VIS} />
          </div>
          <Table testid="traffic-recent" title="Recent visits" rows={d.recent} cols={[
            { key: "ts", label: "When", fmt: (v) => new Date(v).toLocaleString() }, { key: "source", label: "Source" },
            { key: "landing", label: "Landing page" }, { key: "country", label: "Country" },
            { key: "device", label: "Device" }, { key: "pages", label: "Pages", num: true }]} />
          <p className="text-xs text-slate-400">Tip: add <code>?utm_source=newsletter&utm_campaign=launch</code> to links you share to see exactly which ones bring visitors.</p>
        </>
      )}
    </div>
  );
}
